import { useEffect, useRef, useState } from "react";
import { Download, Printer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { pickLocalized } from "../../lib/localized";

export const CARD_SIZES = { "1:1": [1080, 1080], "4:5": [1080, 1350], "9:16": [1080, 1920], landscape: [1200, 630] };

export const STYLE_THEMES = {
  classic: { accent: "#F9A826", bg: ["#FDFBF7", "#F3EAD3"], border: "double", dark: false },
  elegant: { accent: "#B98A1F", bg: ["#FBF7EE", "#EFE3C8"], border: "thin", dark: false },
  minimal: { accent: "#2A9D8F", bg: ["#FFFFFF", "#EEF2F6"], border: "none", dark: false },
  corporate: { accent: "#2F5DA8", bg: ["#F8FAFC", "#E2E8F0"], border: "line", dark: false },
  festive: { accent: "#E89412", bg: ["#FFF6E3", "#FBDFAE"], border: "floral", dark: false },
  traditional: { accent: "#B45309", bg: ["#FFF8EC", "#F3DFB8"], border: "double", dark: false },
  modern: { accent: "#0A192F", bg: ["#FFFFFF", "#E9EEF5"], border: "none", dark: false },
  spiritual: { accent: "#B45309", bg: ["#FFFBEB", "#FBE9C8"], border: "thin", dark: false },
  inspirational: { accent: "#0E7490", bg: ["#ECFEFF", "#CDF3F8"], border: "line", dark: false },
  ngo: { accent: "#2A9D8F", bg: ["#F0FAF8", "#D3EEE9"], border: "thin", dark: false },
  legal: { accent: "#1D3557", bg: ["#F8FAFC", "#E5EAF2"], border: "double", dark: false },
  national: { accent: "#138808", bg: ["#FFFFFF", "#FFE8CC"], border: "tricolor", dark: false },
  floral: { accent: "#C2185B", bg: ["#FFF5F7", "#FADCE5"], border: "floral", dark: false },
  premium: { accent: "#F9A826", bg: ["#0A192F", "#16294A"], border: "gold", dark: true },
  contemporary: { accent: "#6D28D9", bg: ["#FAF5FF", "#EBDFFB"], border: "line", dark: false },
};

function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

function loadImg(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export default function StudioCard({ greeting, org, person, style = "elegant", size = "1:1" }) {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [ready, setReady] = useState(false);
  const [W, H] = CARD_SIZES[size] || CARD_SIZES["1:1"];
  const theme = STYLE_THEMES[style] || STYLE_THEMES.elegant;
  const title = pickLocalized(greeting?.title);
  const message = pickLocalized(greeting?.message);
  const messageHi = pickLocalized(greeting?.message_hi, "hi");
  const quote = pickLocalized(greeting?.quote);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const canvas = ref.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const text = theme.dark ? "#F8FAFC" : "#0A192F";
      const muted = theme.dark ? "#B8C4D6" : "#475569";

      // Background
      const bgImg = await loadImg(greeting.image);
      ctx.clearRect(0, 0, W, H);
      if (bgImg) {
        const scale = Math.max(W / bgImg.width, H / bgImg.height);
        ctx.drawImage(bgImg, (W - bgImg.width * scale) / 2, (H - bgImg.height * scale) / 2, bgImg.width * scale, bgImg.height * scale);
        ctx.fillStyle = theme.dark ? "rgba(10,25,47,0.55)" : "rgba(253,251,247,0.72)";
        ctx.fillRect(0, 0, W, H);
      } else {
        const g = ctx.createLinearGradient(0, 0, W, H);
        g.addColorStop(0, theme.bg[0]);
        g.addColorStop(1, theme.bg[1]);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }

      // Border
      const m = Math.round(W * 0.035);
      ctx.strokeStyle = theme.accent;
      if (theme.border === "double") {
        ctx.lineWidth = W * 0.008; ctx.strokeRect(m, m, W - 2 * m, H - 2 * m);
        ctx.lineWidth = W * 0.002; ctx.strokeRect(m * 1.6, m * 1.6, W - 3.2 * m, H - 3.2 * m);
      } else if (theme.border === "thin" || theme.border === "gold") {
        ctx.lineWidth = theme.border === "gold" ? W * 0.01 : W * 0.003;
        ctx.strokeRect(m, m, W - 2 * m, H - 2 * m);
      } else if (theme.border === "line") {
        ctx.fillStyle = theme.accent;
        ctx.fillRect(0, 0, W, H * 0.012);
        ctx.fillRect(0, H - H * 0.012, W, H * 0.012);
      } else if (theme.border === "tricolor") {
        ctx.fillStyle = "#FF9933"; ctx.fillRect(0, 0, W, H * 0.014);
        ctx.fillStyle = "#138808"; ctx.fillRect(0, H - H * 0.014, W, H * 0.014);
      } else if (theme.border === "floral") {
        ctx.fillStyle = theme.accent + "55";
        for (const [cx, cy] of [[m, m], [W - m, m], [m, H - m], [W - m, H - m]]) {
          for (let i = 0; i < 8; i++) {
            const a = (Math.PI / 4) * i;
            ctx.beginPath();
            ctx.arc(cx + Math.cos(a) * m * 0.9, cy + Math.sin(a) * m * 0.9, m * 0.32, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      // Content
      ctx.textAlign = "center";
      let y = H * (size === "landscape" ? 0.16 : 0.14);
      const cx = W / 2;
      const maxW = W * 0.78;

      if (greeting.date) {
        ctx.font = `700 ${Math.round(W * 0.022)}px Manrope, sans-serif`;
        ctx.fillStyle = theme.accent;
        ctx.fillText(String(greeting.date).toUpperCase(), cx, y);
        y += H * 0.05;
      }

      ctx.font = `900 ${Math.round(W * (size === "landscape" ? 0.055 : 0.068))}px 'Playfair Display', Georgia, serif`;
      ctx.fillStyle = text;
      const hLines = wrapText(ctx, title, maxW);
      hLines.slice(0, 3).forEach((l) => { ctx.fillText(l, cx, y); y += W * 0.075; });
      y += H * 0.015;

      ctx.font = `500 ${Math.round(W * 0.028)}px Manrope, sans-serif`;
      ctx.fillStyle = muted;
      wrapText(ctx, message, maxW).slice(0, 5).forEach((l) => { ctx.fillText(l, cx, y); y += W * 0.042; });
      if (messageHi) {
        y += H * 0.008;
        wrapText(ctx, messageHi, maxW).slice(0, 4).forEach((l) => { ctx.fillText(l, cx, y); y += W * 0.04; });
      }

      if (quote) {
        y += H * 0.02;
        ctx.font = `italic 500 ${Math.round(W * 0.024)}px Georgia, serif`;
        ctx.fillStyle = theme.accent;
        wrapText(ctx, `“${quote}”`, maxW * 0.9).slice(0, 3).forEach((l) => { ctx.fillText(l, cx, y); y += W * 0.036; });
        if (greeting.quote_author) {
          ctx.font = `700 ${Math.round(W * 0.02)}px Manrope, sans-serif`;
          ctx.fillStyle = muted;
          ctx.fillText(`— ${greeting.quote_author}`, cx, y);
          y += W * 0.032;
        }
      }

      // Footer: person + org
      const footY = H - m * 2.4;
      if (person && person.name) {
        const px = m * 2 + W * 0.07;
        const pr = W * 0.052;
        const photo = await loadImg(person.photo);
        ctx.save();
        ctx.beginPath();
        ctx.arc(px, footY - pr, pr, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fillStyle = theme.accent + "33";
        ctx.fill();
        if (photo) {
          ctx.clip();
          const sc = Math.max((pr * 2) / photo.width, (pr * 2) / photo.height);
          ctx.drawImage(photo, px - (photo.width * sc) / 2, footY - pr - (photo.height * sc) / 2, photo.width * sc, photo.height * sc);
        }
        ctx.restore();
        ctx.strokeStyle = theme.accent;
        ctx.lineWidth = W * 0.004;
        ctx.beginPath(); ctx.arc(px, footY - pr, pr, 0, Math.PI * 2); ctx.stroke();
        ctx.textAlign = "left";
        ctx.font = `700 ${Math.round(W * 0.024)}px Manrope, sans-serif`;
        ctx.fillStyle = text;
        ctx.fillText(person.name, px + pr + W * 0.02, footY - pr - W * 0.012);
        ctx.font = `500 ${Math.round(W * 0.019)}px Manrope, sans-serif`;
        ctx.fillStyle = muted;
        const desig = [person.designation, person.designation2].filter(Boolean).join(" | ");
        wrapText(ctx, desig, W * 0.42).slice(0, 2).forEach((l, i) => ctx.fillText(l, px + pr + W * 0.02, footY - pr + W * 0.018 + i * W * 0.026));
      }

      const orgName = org?.organisation || org?.name || "Sahaj Sarathi Foundation";
      ctx.textAlign = person && person.name ? "right" : "center";
      const ox = person && person.name ? W - m * 2 : cx;
      ctx.font = `800 ${Math.round(W * 0.026)}px Manrope, sans-serif`;
      ctx.fillStyle = text;
      ctx.fillText(orgName, ox, footY - W * 0.05);
      ctx.font = `500 ${Math.round(W * 0.02)}px Manrope, sans-serif`;
      ctx.fillStyle = muted;
      const sub = [org?.website, org?.contact || org?.phone].filter(Boolean).join("  •  ");
      if (sub) ctx.fillText(sub, ox, footY - W * 0.022);
      if (org?.footer_text) ctx.fillText(org.footer_text, ox, footY + W * 0.004);

      if (!cancelled) {
        setOverflow(y > footY - W * 0.06);
        setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [greeting, org, person, style, size, W, H, title, message, messageHi, quote, theme.accent, theme.bg, theme.border, theme.dark]);

  const download = (fmt) => {
    try {
      const a = document.createElement("a");
      a.download = `${(title || "greeting").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${size}.${fmt}`;
      a.href = ref.current.toDataURL(fmt === "jpg" ? "image/jpeg" : "image/png", 0.92);
      a.click();
      toast.success(`${fmt.toUpperCase()} downloaded (${W}×${H})`);
    } catch (e) {
      toast.error("Download blocked — upload the background image to the site first");
    }
  };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<img src="${ref.current.toDataURL("image/png")}" style="width:100%" onload="window.print()"/>`);
  };

  return (
    <div data-testid="studio-card">
      {overflow && (
        <p className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2.5 text-xs font-bold text-red-600" data-testid="overflow-warning">
          <AlertTriangle size={14} /> Text overflow risk — shorten content or choose a taller format.
        </p>
      )}
      <canvas ref={ref} width={W} height={H} className="w-full rounded-xl border border-gray-200 shadow-lg" />
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={() => download("png")} disabled={!ready} className="btn-primary !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="card-dl-png"><Download size={14} /> PNG</button>
        <button type="button" onClick={() => download("jpg")} disabled={!ready} className="btn-dark !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="card-dl-jpg"><Download size={14} /> JPG</button>
        <button type="button" onClick={print} disabled={!ready} className="btn-ghost !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="card-print"><Printer size={14} /> Print / PDF</button>
      </div>
    </div>
  );
}
